import {useCallback, useState} from 'react'
import {OfferNode} from '../types'

/**
 * Readiness of a single KPI panel's data (PT-5716).
 *
 * - `ready`   — figures are derivable and the tiles can render.
 * - `loading` — content is being prepared; the panel shows skeleton tiles.
 * - `error`   — figures could not be derived or fetched; the panel shows a retry card.
 *
 * Data is read from the in-memory offer node today, so a panel is effectively always `ready`.
 * The three-state shape is deliberate scaffolding: when these figures move to a real API on the
 * production path, the fetch's loading/error can feed straight into this same hook and UI without
 * a rewrite. QA can exercise the not-yet-reachable states today via {@link KPI_FORCE_PARAM}.
 */
export type KpiPanelStatus = 'ready' | 'loading' | 'error'

/**
 * URL query parameter QA appends to force a KPI panel state, e.g. `?pammKpiForce=error`.
 * Honored in every environment except production, so it can be used on staging to verify the
 * skeleton and failure states without touching data, yet can never fire for a real live user.
 */
export const KPI_FORCE_PARAM = 'pammKpiForce'

/** True only in a production build; the QA force flag is ignored there. */
const isProductionEnv = (): boolean => {
  try {
    // Runtime config is injected on `window`; absent in unit tests (treated as non-production).
    return (window as {__BD_CONFIG?: {environment?: string}}).__BD_CONFIG?.environment === 'production'
  } catch {
    return false
  }
}

/**
 * Reads a forced KPI panel state from a URL query string, honoring it only outside production.
 *
 * @param search - The `window.location.search` string (e.g. `?pammKpiForce=loading`).
 * @param productionBuild - Whether this is a production build (the flag is ignored when true).
 * @returns The forced status, or `null` when no valid flag is present or the build is production.
 */
export const readForcedKpiState = (search: string, productionBuild: boolean): KpiPanelStatus | null => {
  if (productionBuild) return null
  const forced = new URLSearchParams(search).get(KPI_FORCE_PARAM)
  return forced === 'loading' || forced === 'error' ? forced : null
}

/** Whether the panel's figures can be derived from the node at all. */
const isDerivable = (node: OfferNode | null | undefined): boolean => node?.offer != null

/**
 * Resolves a KPI panel's readiness plus a retry action (PT-5716).
 *
 * Precedence: a QA-forced state (via {@link KPI_FORCE_PARAM}) wins until the user retries; otherwise
 * the panel is `ready` when its figures are derivable and `error` when they are not. `retry` clears
 * any forced state for this panel and re-evaluates derivability, so QA sees the panel recover from a
 * forced failure and a genuinely underivable node re-attempts on click.
 *
 * @param node - The offer node the panel describes.
 * @returns The current {@link KpiPanelStatus} and a `retry` callback.
 */
export const useKpiPanelState = (node: OfferNode | null | undefined): {status: KpiPanelStatus; retry: () => void} => {
  // Bumped by retry: clears the forced state and forces derivability to be re-read.
  const [retried, setRetried] = useState(false)

  const forced = retried ? null : readForcedKpiState(window.location.search, isProductionEnv())
  const status: KpiPanelStatus = forced ?? (isDerivable(node) ? 'ready' : 'error')

  const retry = useCallback(() => setRetried(true), [])

  return {status, retry}
}
